import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Users, Plus, CheckCircle2, Search, AlertCircle } from 'lucide-react';

const SelectEmployeeModal = ({ show, handleClose, handleSelect, employees, existingPayslips = [] }) => {
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Reset selections when modal opens/closes
    useEffect(() => {
        if (!show) {
            setSelectedEmployees([]);
            setSelectAll(false);
            setSearchTerm('');
        }
    }, [show]);

    // Create a Set of employee IDs that already have payslips
    const employeesWithPayslips = useMemo(() => {
        return new Set(existingPayslips.map(payslip => payslip.employeeId || payslip.employee_id));
    }, [existingPayslips]);

    // Filter employees based on search term
    const filteredEmployees = employees.filter(employee =>
        employee.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Separate employees into eligible and ineligible categories
    const { eligibleEmployees, ineligibleEmployees } = useMemo(() => {
        return filteredEmployees.reduce((acc, employee) => {
            const employeeId = employee.employee_id || employee.id;
            const hasContract = employee.has_active_contract;
            const alreadyHasPayslip = employeesWithPayslips.has(employeeId);

            if (hasContract && !alreadyHasPayslip) {
                acc.eligibleEmployees.push(employee);
            } else {
                acc.ineligibleEmployees.push({
                    ...employee,
                    ineligibleReason: alreadyHasPayslip ? 'already_added' : 'no_contract'
                });
            }
            return acc;
        }, { eligibleEmployees: [], ineligibleEmployees: [] });
    }, [filteredEmployees, employeesWithPayslips]);

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);
        // Only select employees with active contracts
        setSelectedEmployees(isChecked ? eligibleEmployees.map(emp => emp.employee_id) : []);
    };

    const handleSelectChange = (employee, event) => {
        const employeeId = employee.employee_id || employee.id;
        const alreadyHasPayslip = employeesWithPayslips.has(employeeId);

        // Prevent selection if no active contract or already has payslip
        if (!employee.has_active_contract || alreadyHasPayslip) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        const value = employeeId;
        setSelectedEmployees(prevState => {
            const newState = prevState.includes(value)
                ? prevState.filter(id => id !== value)
                : [...prevState, value];

            // Update selectAll state based on all eligible employees being selected
            setSelectAll(newState.length === eligibleEmployees.length && eligibleEmployees.length > 0);
            return newState;
        });
    };

    return (
        <>
            <style>{`
                .modern-employee-modal .modal-content {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
                }
                
                .modern-employee-modal .modal-header {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    border-bottom: 1px solid #e5e7eb;
                    border-radius: 12px 12px 0 0;
                    padding: 2rem 2rem 1.5rem 2rem;
                }
                
                .modern-employee-modal .modal-title {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }
                
                .modern-employee-modal .modal-body {
                    padding: 2rem;
                }
                
                .modern-employee-modal .modal-footer {
                    background: #f8fafc;
                    border-top: 1px solid #e5e7eb;
                    border-radius: 0 0 12px 12px;
                    padding: 1.5rem 2rem;
                    display: flex;
                    gap: 1rem;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .search-container {
                    position: relative;
                    margin-bottom: 1.5rem;
                }
                
                .search-input {
                    padding-left: 2.5rem;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    padding-top: 0.75rem;
                    padding-bottom: 0.75rem;
                    padding-right: 1rem;
                    width: 100%;
                    font-size: 0.875rem;
                    transition: all 0.2s ease;
                }
                
                .search-input:focus {
                    outline: none;
                    border-color: var(--bs-primary);
                    box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
                }
                
                .search-icon {
                    position: absolute;
                    left: 0.75rem;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #9ca3af;
                    pointer-events: none;
                }
                
                .select-all-container {
                    background: #f0fdf4;
                    border: 1px solid #a7f3d0;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .select-all-checkbox {
                    width: 18px;
                    height: 18px;
                    accent-color: var(--bs-primary);
                    cursor: pointer;
                }

                .select-all-label {
                    font-weight: 600;
                    color: #14532d;
                    cursor: pointer;
                    margin: 0;
                    user-select: none;
                }
                
                .employees-list {
                    max-height: 400px;
                    overflow-y: auto;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    background: white;
                }
                
                .employee-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1rem;
                    border-bottom: 1px solid #f3f4f6;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                
                .employee-item:last-child {
                    border-bottom: none;
                }
                
                .employee-item:hover {
                    background: #f8fafc;
                }
                
                .employee-item.selected {
                    background: #f0fdf4;

                }
                
                .employee-item.disabled {
                    background: #fafafa;
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .employee-item.disabled:hover {
                    background: #fafafa;
                }
                
                .employee-checkbox {
                    width: 18px;
                    height: 18px;
                    accent-color: var(--bs-primary);
                    cursor: pointer;
                    flex-shrink: 0;
                }
                
                .employee-checkbox:disabled {
                    cursor: not-allowed;
                    opacity: 0.5;
                }
                
                .employee-name {
                    font-weight: 500;
                    color: #1e293b;
                    cursor: pointer;
                    user-select: none;
                    flex: 1;
                }
                
                .employee-name.disabled {
                    color: #94a3b8;
                    cursor: not-allowed;
                }
                
                .employee-badge {
                    background: #10b981;
                    color: white;
                    border-radius: 50%;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                
                .warning-badge {
                    background: #fef3c7;
                    color: #92400e;
                    border: 1px solid #fbbf24;
                    border-radius: 12px;
                    padding: 0.25rem 0.625rem;
                    font-size: 0.75rem;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    flex-shrink: 0;
                }
                
                .ineligible-banner {
                    background: #fef3c7;
                    border: 1px solid #fbbf24;
                    border-radius: 8px;
                    padding: 0.875rem 1rem;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                }
                
                .ineligible-banner-icon {
                    color: #f59e0b;
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }
                
                .ineligible-banner-content {
                    flex: 1;
                }
                
                .ineligible-banner-title {
                    font-weight: 600;
                    color: #92400e;
                    margin: 0 0 0.25rem 0;
                    font-size: 0.875rem;
                }
                
                .ineligible-banner-text {
                    color: #92400e;
                    margin: 0;
                    font-size: 0.8125rem;
                }
                
                .selection-summary {
                    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
                    border: 1px solid #86efac;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .summary-text {
                    color: #166534;
                    font-weight: 500;
                    font-size: 0.875rem;
                }
                
                .summary-count {
                    background: var(--bs-primary);
                    color: white;
                    border-radius: 16px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                
                .modern-btn {
                    border-radius: 6px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 500;
                    font-size: 0.875rem;
                    border: none;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                }
                
                .btn-modern-secondary {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }
                
                .btn-modern-secondary:hover {
                    background: #e2e8f0;
                    color: #475569;
                }
                
                .btn-modern-primary {
                    background: var(--bs-primary);
                    color: white;
                }

                .btn-modern-primary:hover:not(:disabled) {
                    background: var(--primary-hover);
                    transform: translateY(-1px);
                }
                
                .btn-modern-primary:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                }
                
                .empty-state {
                    text-align: center;
                    padding: 3rem 1rem;
                    color: #64748b;
                }
                
                .empty-state-icon {
                    margin-bottom: 1rem;
                    opacity: 0.5;
                }
                
                .info-text {
                    font-size: 0.875rem;
                    color: #64748b;
                    line-height: 1.5;
                    margin-bottom: 1.5rem;
                }
                
                /* Custom scrollbar */
                .employees-list::-webkit-scrollbar {
                    width: 6px;
                }
                
                .employees-list::-webkit-scrollbar-track {
                    background: #f1f5f9;
                }
                
                .employees-list::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }
                
                .employees-list::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
                
                @media (max-width: 768px) {
                    .modern-employee-modal .modal-header,
                    .modern-employee-modal .modal-body,
                    .modern-employee-modal .modal-footer {
                        padding: 1.5rem 1rem;
                    }
                    
                    .employees-list {
                        max-height: 300px;
                    }
                }
            `}</style>

            <Modal show={show} onHide={handleClose} size="lg" className="modern-employee-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Users size={24} color="var(--bs-primary)" />
                        Välj anställda till lönekörning
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p className="info-text">
                        Välj vilka anställda som ska inkluderas i denna lönekörning. Du kan söka efter namn
                        eller använda "Välj alla" för att inkludera alla aktiva anställda.
                    </p>

                    {/* Search */}
                    <div className="search-container">
                        <Search size={18} className="search-icon" />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Sök efter anställd..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Ineligible Employees Warning 
                    {ineligibleEmployees.length > 0 && (
                        <div className="ineligible-banner">
                            <AlertCircle size={20} className="ineligible-banner-icon" />
                            <div className="ineligible-banner-content">
                                <p className="ineligible-banner-title">
                                    {ineligibleEmployees.length} anställd{ineligibleEmployees.length > 1 ? 'a' : ''} kan inte läggas till
                                </p>
                                <p className="ineligible-banner-text">
                                    {ineligibleEmployees.filter(e => e.ineligibleReason === 'no_contract').length > 0 && (
                                        <>
                                            <strong>{ineligibleEmployees.filter(e => e.ineligibleReason === 'no_contract').length}</strong> saknar aktivt anställningskontrakt.
                                            {' '}
                                        </>
                                    )}
                                    {ineligibleEmployees.filter(e => e.ineligibleReason === 'already_added').length > 0 && (
                                        <>
                                            <strong>{ineligibleEmployees.filter(e => e.ineligibleReason === 'already_added').length}</strong> har redan en lönespecifikation i denna period.
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    )}*/}

                    {/* Select All */}
                    <div className="select-all-container">
                        <input
                            type="checkbox"
                            id="selectAll"
                            className="select-all-checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            disabled={eligibleEmployees.length === 0}
                        />
                        <label htmlFor="selectAll" className="select-all-label">
                            Välj alla ({eligibleEmployees.length} tillgänglig{eligibleEmployees.length !== 1 ? 'a' : ''})
                            {ineligibleEmployees.length > 0 && ` • ${ineligibleEmployees.length} otillgänglig${ineligibleEmployees.length !== 1 ? 'a' : ''}`}
                        </label>
                    </div>

                    {/* Employees List */}
                    <div className="employees-list">
                        {filteredEmployees.length === 0 ? (
                            <div className="empty-state">
                                <Users size={48} className="empty-state-icon" />
                                <p>{searchTerm ? 'Inga anställda matchar din sökning' : 'Inga anställda tillgängliga'}</p>
                            </div>
                        ) : (
                            filteredEmployees.map((employee) => {
                                const employeeId = employee.employee_id || employee.id;
                                const isSelected = selectedEmployees.includes(employeeId);
                                const hasContract = employee.has_active_contract;
                                const alreadyHasPayslip = employeesWithPayslips.has(employeeId);
                                const isDisabled = !hasContract || alreadyHasPayslip;

                                // Determine tooltip and badge text
                                let tooltipText = '';
                                let badgeText = '';
                                if (alreadyHasPayslip) {
                                    tooltipText = 'Lönespecifikation finns redan för denna anställd i denna period';
                                    badgeText = 'Redan tillagd';
                                } else if (!hasContract) {
                                    tooltipText = 'Anställd saknar aktivt kontrakt';
                                    badgeText = 'Inget kontrakt';
                                }

                                return (
                                    <div
                                        key={employeeId}
                                        className={`employee-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                            handleSelectChange(employee, e);
                                        }}
                                        title={tooltipText}
                                    >
                                        <input
                                            type="checkbox"
                                            className="employee-checkbox"
                                            value={employeeId}
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={(e) => handleSelectChange(employee, e)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <span className={`employee-name ${isDisabled ? 'disabled' : ''}`}>
                                            {employee.name}
                                        </span>
                                        {isSelected && !isDisabled && (
                                            <div className="employee-badge">
                                                <CheckCircle2 size={14} />
                                            </div>
                                        )}
                                        {isDisabled && (
                                            <div className="warning-badge">
                                                <AlertCircle size={12} />
                                                {badgeText}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Selection Summary 
                    {selectedEmployees.length > 0 && (
                        <div className="selection-summary">
                            <span className="summary-text">
                                Valda anställda kommer att få lönespecifikationer skapade
                            </span>
                            <span className="summary-count">
                                {selectedEmployees.length} valda
                            </span>
                        </div>
                    )}*/}
                </Modal.Body>

                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <div className="d-flex gap-2">
                            <button className="modern-btn btn-modern-secondary" onClick={handleClose}>
                                Avbryt
                            </button>
                            <button
                                className="modern-btn btn-modern-primary"
                                onClick={() => { handleSelect(selectedEmployees); }}
                                disabled={selectedEmployees.length === 0}
                            >
                                <Plus size={16} />
                                Lägg till ({selectedEmployees.length})
                            </button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default SelectEmployeeModal;
